import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Container, IconButton, List, Typography } from '@mui/material';
import { Icon } from 'antd';
import useAuth from 'context/useAuth';
import useToggle from 'hooks/useToggle';

import FocusableHeader from 'components/focusableheader/FocusableHeader';
import GreetingBanner from 'components/greetingBanner/GreetingBanner';
import TooltipChangeNameModal from 'components/modals/TooltipChangeNameModal';
import { AppRoutes } from 'routes';
import { formatPhoneNumber } from 'utils/Formatter';

import { CurrentAddress } from './CurrentAddress/CurrentAddress';
import ProfileConstructor from './ProfileConstructor';

const Profile = () => {
    const { userProfile } = useAuth();
    const [displayChangeNameModal, toggleChangeNameModal] = useToggle();
    const { formatMessage } = useIntl();

    return (
        <Box className="profile">
            <FocusableHeader className="page-title">
                <FormattedMessage id="pages.profile.title" />
            </FocusableHeader>
            <Box className="greetings-banner-container">
                <GreetingBanner ownersName={userProfile.ownerName} />
                <IconButton onClick={toggleChangeNameModal} title={formatMessage({ id: 'pages.profile.tooltip.hint' })}>
                    <Icon className="popup-hint" id="info-icon" type="info-circle" />
                </IconButton>
            </Box>
            <Container className="profile-contents-container">
                <Box className="personal-settings-section">
                    <Typography className="section-title">
                        <FormattedMessage id="pages.profile.personal.settings" />
                    </Typography>
                    <List className="personal-settings">
                        <ProfileConstructor ariaLabel="pages.profile.edit.email" routeId={AppRoutes.profileEmail}>
                            <Typography className="item-label">
                                <FormattedMessage id="pages.profile.email" />
                            </Typography>
                            <Typography className="item-value">{userProfile.email}</Typography>
                        </ProfileConstructor>
                        <ProfileConstructor ariaLabel="pages.profile.edit.phone" routeId={AppRoutes.profilePhone}>
                            <Typography className="item-label">
                                <FormattedMessage id="pages.profile.phone" />
                            </Typography>
                            <React.Fragment>
                                <Typography className="item-label-child">
                                    <FormattedMessage id="pages.profile.phone.mobile" />
                                    <FormattedMessage id="pages.profile.phone.primary" />
                                </Typography>
                                <Typography className="item-value">
                                    {formatPhoneNumber(userProfile.mobilePhone)}
                                </Typography>
                            </React.Fragment>
                            {userProfile.secondaryPhone && (
                                <React.Fragment>
                                    <Typography className="item-label-child">
                                        <FormattedMessage id="pages.profile.phone.secondary" />
                                        <FormattedMessage id="pages.profile.phone.optional" />
                                    </Typography>
                                    <Typography className="item-value">
                                        {formatPhoneNumber(userProfile.secondaryPhone)}
                                    </Typography>
                                </React.Fragment>
                            )}
                        </ProfileConstructor>
                        <ProfileConstructor ariaLabel="pages.profile.edit.address" routeId={AppRoutes.profileAddress}>
                            <Typography className="item-label">
                                <FormattedMessage id="pages.profile.address" />
                            </Typography>
                            <CurrentAddress userProfile={userProfile} />
                        </ProfileConstructor>
                        <ProfileConstructor ariaLabel="pages.profile.edit.language" routeId={AppRoutes.profileLanguage}>
                            <Typography className="item-label">
                                <FormattedMessage id="pages.profile.language" />
                            </Typography>
                            <Typography className="item-value">
                                <FormattedMessage
                                    id={`pages.profile.language.${userProfile.language === 'E' ? 'english' : 'french'}`}
                                />
                            </Typography>
                        </ProfileConstructor>
                    </List>
                </Box>
                <Box className="account-settings-section">
                    <Typography className="section-title">
                        <FormattedMessage id="pages.profile.logging.to.fairstone.account" />
                    </Typography>
                    <List className="account-settings">
                        <ProfileConstructor
                            ariaLabel="pages.profile.change.your.password"
                            routeId={AppRoutes.profilePassword}
                        >
                            <Typography className="item-label">
                                <FormattedMessage id="pages.profile.change.your.password" />
                            </Typography>
                        </ProfileConstructor>
                    </List>
                </Box>
            </Container>
            <TooltipChangeNameModal onOk={toggleChangeNameModal} visible={displayChangeNameModal} />
        </Box>
    );
};

export default Profile;
